import { useEffect, useMemo, useState, useReducer } from 'preact/hooks'
import { useDebouncedValue } from '@/hooks'
import { useLocation } from 'preact-iso'
import { routes } from '@/utils/routes'
import { audienceApi } from '@/api/audience'
import type { CampaignListAnalyticsResponseDto, CampaignListAnalyticsDto, FileImportRowGridDto } from '@/types/campaignAnalytics'
import { lazy, Suspense } from 'preact/compat'
import { useTheme } from '@/hooks/useTheme'
import { CampaignListContactsSection } from './campaigns/campaignList/CampaignListContactsSection'
import { ContactPeekFlyout } from './contacts/ContactPeekFlyout'
import { useEnrichmentEvent } from '@/hooks/useEnrichmentEvent'
import { usePageHeader } from '@/hooks/usePageHeader'
import { RefreshCw } from 'lucide-preact'
import { useAIChatContextUpdater } from '@/hooks'
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat'
import AudienceViewToggle from '@/components/common/AudienceViewToggle'
import { DEFAULT_AUDIENCE_VIEW, filterLeadsByAudience } from '@/utils/leadAudienceFilter'

const CampaignListCharts = lazy(() => import('./campaigns/campaignList/CampaignListCharts').then(m => ({ default: m.CampaignListCharts })))

interface AudienceDetailProps { clientCompanyId: string; selectedWorkspaceId: string; campaignId: string; fileImportId: string }

export function AudienceDetail({ clientCompanyId, selectedWorkspaceId, campaignId, fileImportId }: AudienceDetailProps) {
    const location = useLocation()
    const { setHeader } = usePageHeader()
    const { resolvedTheme } = useTheme()
    const [initialLoading, setInitialLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [analytics, setAnalytics] = useState<CampaignListAnalyticsDto | null>(null)
    const [contacts, setContacts] = useState<FileImportRowGridDto[]>([])
    const [selectedContact, setSelectedContact] = useState<FileImportRowGridDto | null>(null)

    // Live-flip the audience grid's enrichment cell when a row's enrichment finishes (ORB-383).
    useEnrichmentEvent((evt) => {
        if (!evt.fileImportRowId) return
        setContacts(prev => prev.map(c => c.Id === evt.fileImportRowId ? { ...c, EnrichmentStatus: evt.enrichmentStatus } : c))
    })
    const [, forceUpdate] = useReducer((x: number) => x + 1, 0)
    const { updateContext } = useAIChatContextUpdater()

    const [activeTab, setActiveTab] = useState<'contacts' | 'analytics'>('contacts')
    const [search, setSearch] = useState('')
    const [country, setCountry] = useState('')
    const [showNonProspects, setShowNonProspects] = useState(false)

    const debouncedSearch = useDebouncedValue(search, 250)

    // Load analytics and contacts
    const loadData = async (isRefresh = false, signal?: AbortSignal) => {
        // Fallback: if somehow missing, redirect to audience index (could alternatively 404)
        if (!fileImportId) {
            location.route(routes.scoped(clientCompanyId, selectedWorkspaceId).audienceIndex())
            return
        }
        try {
            if (isRefresh) {
                setRefreshing(true)
            } else {
                setInitialLoading(true)
            }
            setError(null)
            const resp = await audienceApi.getAnalytics(clientCompanyId, selectedWorkspaceId, campaignId, fileImportId, signal)
            if (resp.error) { setError(resp.error); return }
            const data = resp.data as CampaignListAnalyticsResponseDto
            setAnalytics(data.Analytics ?? null)
            setContacts(data.FileRows || [])
        } catch (e) {
            // Don't show error if request was aborted
            if (e instanceof Error && e.name === 'AbortError') return
            console.error(e);
            setError('Failed to load audience analytics')
        } finally {
            if (isRefresh) {
                setRefreshing(false)
            } else {
                setInitialLoading(false)
            }
        }
    }

    // Fetch analytics for selected fileImportId (assumed required by route)
    useEffect(() => {
        const abortController = new AbortController()
        loadData(false, abortController.signal)
        return () => abortController.abort()
    }, [fileImportId, campaignId])

    // The audience rule runs first so search and country narrow what is actually on screen. The
    // audience view is fixed: every row of a file import was targeted by definition, so the
    // three-state selector has nothing to slice here and only the non-prospect switch is rendered.
    const audienceFiltered = useMemo(
        () => filterLeadsByAudience(contacts, DEFAULT_AUDIENCE_VIEW, showNonProspects),
        [contacts, showNonProspects],
    )

    const nonProspectCount = useMemo(() => contacts.filter(c => c.IsNonProspect).length, [contacts])

    // Update AI context when analytics data loads
    useEffect(() => {
        if (!analytics) return

        updateContext({
            campaignId,
            location: ChatLocations.CampaignAudience,
            entityType: ChatEntityTypes.Campaign,
            entityId: campaignId,
            viewMode: ChatViewModes.View,
            availableFields: [
                { name: 'totalContacts', displayName: 'Total Contacts', fieldType: 'number', currentValue: analytics.Total.toString(), isEditable: false, isFocused: false },
                { name: 'contactsLoaded', displayName: 'Contacts Loaded', fieldType: 'number', currentValue: audienceFiltered.length.toString(), isEditable: false, isFocused: false },
                { name: 'activeTab', displayName: 'Active Tab', fieldType: 'text', currentValue: activeTab, isEditable: false, isFocused: false },
            ],
            availableActions: [
                'View Contact Details',
                'Filter Contacts',
                'View Analytics',
                'Refresh Data',
            ],
        })
    }, [analytics, audienceFiltered.length, activeTab, selectedWorkspaceId, campaignId, updateContext])

    // Derived from the rows actually reachable, or the select offers countries that only hidden
    // people are in and picking one silently yields nothing.
    const countryOptions = useMemo(() => { const set = new Set<string>(); for (const c of audienceFiltered) { if (c.Country) set.add(c.Country) } return Array.from(set).sort() }, [audienceFiltered])
    // City dimension removed (City field dropped)

    const filtered = useMemo(() => {
        return audienceFiltered.filter(c => {
            if (debouncedSearch) { const q = debouncedSearch.toLowerCase(); const target = `${c.FirstName || ''} ${c.LastName || ''} ${c.Email || ''} ${c.JobTitle || ''} ${c.CompanyName || ''} ${c.Country || ''}`.toLowerCase(); if (!target.includes(q)) return false }
            if (country && c.Country !== country) return false
            return true
        })
    }, [audienceFiltered, debouncedSearch, country])

    // Clearing a country that the audience filter has just taken off the list. Left set, the select
    // renders blank over an empty grid with nothing on screen saying a filter is still applied.
    useEffect(() => {
        if (country && !countryOptions.includes(country)) setCountry('')
    }, [countryOptions, country])

    // A flyout left open on a row the filter has just hidden would be showing someone who is no
    // longer in the grid behind it. Same guard the leads screens carry.
    useEffect(() => {
        if (selectedContact && !filtered.some(c => c.Id === selectedContact.Id)) setSelectedContact(null)
    }, [filtered, selectedContact])

    const palette = ['#2563eb', '#059669', '#f59e0b', '#ef4444', '#7c3aed', '#06b6d4', '#84cc16', '#f97316', '#db2777', '#0ea5e9', '#10b981', '#3b82f6', '#8b5cf6', '#eab308', '#22c55e', '#14b8a6', '#e11d48', '#a855f7', '#f43f5e', '#38bdf8']

    const nivoTheme = useMemo(() => { const isDark = resolvedTheme === 'dark'; const axisText = isDark ? '#cbd5e1' : '#334155'; const gridLine = isDark ? '#334155' : '#e2e8f0'; const legendText = isDark ? '#e2e8f0' : '#334155'; return { background: 'transparent', text: { fill: axisText }, axis: { domain: { line: { stroke: gridLine } }, ticks: { line: { stroke: gridLine }, text: { fill: axisText } }, legend: { text: { fill: legendText } } }, grid: { line: { stroke: gridLine, strokeDasharray: '4 4' } }, legends: { text: { fill: legendText } }, tooltip: { container: { background: isDark ? '#0f172a' : '#ffffff', color: isDark ? '#e2e8f0' : '#0f172a' } } } as unknown as Record<string, unknown> }, [resolvedTheme])

    // Determine origin from query (?origin=campaign|audience-index)
    const origin = (() => {
        try {
            const url = new URL(location?.url || '/', window.location.origin)
            return url.searchParams.get('origin') || 'audience-index'
        } catch { return 'audience-index' }
    })()

    // Breadcrumbs
    useEffect(() => {
        const r = routes.scoped(clientCompanyId, selectedWorkspaceId)
        const items = origin === 'campaign'
            ? [
                { text: 'Campaigns', href: r.campaignsIndex() },
                { text: 'Campaign', href: r.campaign(campaignId, { tab: 'audience' }) },
                { text: 'Audience' }
            ]
            : [
                { text: 'Audience', href: r.audienceIndex() },
                { text: 'Detail' }
            ]
        setHeader({ items })
    }, [clientCompanyId, selectedWorkspaceId, campaignId, origin])

    if (initialLoading) {
        return (
            <div className="p-6">
                <div className="flex items-center justify-center py-12">
                    <div className="flex items-center gap-3">
                        <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                        <span className="text-slate-600 dark:text-slate-300">Loading audience analytics...</span>
                    </div>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="text-slate-700 dark:text-slate-200">{error}</div>
            </div>
        )
    }

    return (
        <div className="p-6">
            <div className="mb-6">
                <h2 className="text-2xl font-semibold text-slate-900 dark:text-white">Audience Details</h2>
                <p className="text-slate-600 dark:text-slate-300 mt-1">Analytics and contacts for the campaign audience.</p>
            </div>

            <div className="mb-6 flex items-center gap-2 border-b border-slate-200 dark:border-slate-700">
                <button
                    type="button"
                    onClick={() => setActiveTab('contacts')}
                    className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${activeTab === 'contacts' ? 'border-primary-600 text-primary-600 dark:text-primary-400' : 'border-transparent text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'}`}
                >Contacts</button>
                <button
                    type="button"
                    onClick={() => setActiveTab('analytics')}
                    className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${activeTab === 'analytics' ? 'border-primary-600 text-primary-600 dark:text-primary-400' : 'border-transparent text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'}`}
                >Analytics</button>
            </div>

            {activeTab === 'analytics' && (
                analytics ? (
                    <div className="relative">
                        {refreshing && (
                            <div className="absolute inset-0 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
                                <div className="flex items-center gap-3">
                                    <div className="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                                    <span className="text-sm text-slate-600 dark:text-slate-300">Refreshing...</span>
                                </div>
                            </div>
                        )}
                        <div className="mb-4 flex justify-end">
                            <button
                                onClick={() => loadData(true)}
                                disabled={refreshing}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-md hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
                                title="Refresh from server"
                            >
                                <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
                                Refresh
                            </button>
                        </div>
                        <Suspense fallback={<div className="p-4 text-slate-600 dark:text-slate-300">Loading charts…</div>}>
                            <CampaignListCharts analytics={analytics} palette={palette} nivoTheme={nivoTheme} />
                        </Suspense>
                    </div>
                ) : (
                    <div className="p-6 text-sm text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">No analytics loaded.</div>
                )
            )}

            {activeTab === 'contacts' && (
                <>
                <div className="mb-4 flex items-center justify-between gap-4 flex-wrap">
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                        {showNonProspects
                            ? 'Showing only people on a non-prospect domain.'
                            : 'Non-prospects are hidden, matching the Analytics tab.'}
                    </p>
                    <AudienceViewToggle
                        showNonProspects={showNonProspects}
                        onToggleNonProspects={() => setShowNonProspects(v => !v)}
                        nonProspectCount={nonProspectCount}
                        hideAudienceViews
                    />
                </div>
                <CampaignListContactsSection
                    filtered={filtered}
                    search={search}
                    setSearch={setSearch}
                    country={country}
                    setCountry={setCountry}
                    countryOptions={countryOptions}
                    clientCompanyId={clientCompanyId}
                    workspaceId={selectedWorkspaceId}
                    campaignId={campaignId}
                    onSelectContact={(c) => setSelectedContact(c)}
                    onContactUpdated={(updated) => {
                        setContacts(prev => prev.map(ct => ct.Id === updated.Id ? { ...ct, ...updated } : ct))
                        if (selectedContact?.Id === updated.Id) {
                            setSelectedContact(prev => prev ? { ...prev, ...updated } : null)
                        }
                        // Force re-render after state update
                        queueMicrotask(() => forceUpdate(0))
                    }}
                    onRefresh={() => loadData(true)}
                    refreshing={refreshing}
                />
                </>
            )}

            {selectedContact && (
                <ContactPeekFlyout
                    anchorType="audience"
                    anchorId={selectedContact.Id}
                    clientCompanyId={clientCompanyId}
                    workspaceId={selectedWorkspaceId}
                    campaignId={campaignId}
                    onClose={() => setSelectedContact(null)}
                />
            )}
        </div>
    )
}

export default AudienceDetail
